// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { XNGAGE_ELEMENTS } from "@eisinc/XDL/constants";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, xParserOptions } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = OwnProps;

interface ManufacturerCertificatesStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    text?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const manufacturerCertificatesStyles: ManufacturerCertificatesStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            border: 1px solid ${color("common.border")};
            background-color: ${color("common.ui1")};
            padding: 12px;
        `,
    },
    title: {
        variant: "h3",
        forwardAs: "p",
        css: css`
            margin-bottom: 0;
        `,
    },
    text: {
        ...fontConfig.small,
        css: css`
            ${TypographyStyle} {
                margin-bottom: 0;
                font-size: ${fontConfig.small.size}px;
                line-height: ${fontConfig.small.lineHeight};
            }
        `,
    },
    link: {
        typographyProps: {
            size: fontConfig.singleLineRegular.size,
            weight: fontConfig.weight.medium,
            lineHeight: fontConfig.singleLineRegular.lineHeight,
            css: css`
                && {
                    line-height: ${fontConfig.singleLineRegular.lineHeight};
                }
            `,
        },
    },
};

const enum fields {
    title = "title",
    text = "text",
    link = "link",
    linkText = "linkText",
    openInNewWindow = "openInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.text]: string;
        [fields.link]: LinkFieldValue;
        [fields.linkText]: string;
        [fields.openInNewWindow]: boolean;
    };
}
const styles = manufacturerCertificatesStyles;

const ManufacturerCertificates: FC<Props> = ({ fields: { title, text, link, linkText, openInNewWindow } }) => {
    const { url, title: linkTitle } = useGetLink(link);

    return (
        <StyledWrapper {...styles.container}>
            {title && <Typography {...styles.title}>{title}</Typography>}
            {text && <Typography {...styles.text}>{parse(text, xParserOptions)}</Typography>}
            {url && (
                <Link {...styles.link} href={url} title={linkTitle} target={openInNewWindow ? "_blank" : "_self"}>
                    {linkText || linkTitle}
                </Link>
            )}
        </StyledWrapper>
    );
};

const ManufacturerCertificatesWidget: WidgetModule = {
    component: ManufacturerCertificates,
    definition: {
        group: XNGAGE_ELEMENTS as any,
        fieldDefinitions: [
            {
                editorTemplate: "TextField",
                name: fields.title,
                defaultValue: "Manufacturer Certificates",
                fieldType: "General",
            },
            {
                editorTemplate: "RichTextField",
                name: fields.text,
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.link,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.linkText,
                editorTemplate: "TextField",
                defaultValue: "Find Manufacturer Certificates",
                fieldType: "General",
            },
            {
                name: fields.openInNewWindow,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};
export default ManufacturerCertificatesWidget;
